'use client';

import { cn } from '@/lib/utils';
import { podVariant } from '../types';
import { useChartContext } from '../ChartContext';

interface Props {
  variants: string[];
  selectedVariants: Set<string>;
  toggleVariant: (v: string) => void;
  selectAllVariants: () => void;
  downsamplingEnabled: boolean;
  toggleDownsampling: () => void;
}

export function FloatingToolbar(p: Props) {
  const { podColors, hiddenPods, setHidden, isolatePod, showAll } = useChartContext();
  // A variant filter is active when at least one variant is explicitly selected
  // and not every known variant is selected.
  const variantFilterActive =
    p.selectedVariants.size > 0 && !p.variants.every((v) => p.selectedVariants.has(v));
  const isVariantHidden = (pod: string) =>
    variantFilterActive && !p.selectedVariants.has(podVariant(pod));
  const allPodsVisible =
    hiddenPods.size === 0 &&
    (!variantFilterActive || Object.keys(podColors).every((pod) => !isVariantHidden(pod)));
  const allVariantsOn = p.selectedVariants.size === 0 || p.variants.every((v) => p.selectedVariants.has(v));
  const podEntries = Object.entries(podColors);
  const filterButtonClasses = (active: boolean) => cn(
    'inline-flex h-6 items-center rounded-md border px-2.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    active
      ? 'border-primary bg-primary/10 text-primary'
      : 'bg-background text-muted-foreground hover:bg-muted',
  );

  return (
    <div>
      <div className="mb-2 flex min-h-6 flex-wrap items-center gap-2">
        {/* Variant filter buttons */}
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-1 text-xs text-muted-foreground">Pods</span>
          {p.variants.length > 0 && (
            <>
              <button
                type="button"
                aria-pressed={allVariantsOn}
                className={filterButtonClasses(allVariantsOn)}
                onClick={p.selectAllVariants}
              >
                All Pods
              </button>
              {p.variants.map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={p.selectedVariants.has(v)}
                  className={filterButtonClasses(p.selectedVariants.has(v))}
                  onClick={() => p.toggleVariant(v)}
                >
                  {v}
                </button>
              ))}
            </>
          )}
        </div>
        <button
          type="button"
          aria-pressed={!p.downsamplingEnabled}
          className={cn(filterButtonClasses(!p.downsamplingEnabled), 'ml-auto')}
          onClick={p.toggleDownsampling}
          title={p.downsamplingEnabled
            ? 'Render every fetched data point; this may make the page slower'
            : 'Limit chart rendering to 4,000 data points'}
        >
          {p.downsamplingEnabled ? 'Disable downsampling' : 'Enable downsampling'}
        </button>
      </div>

      {/* Shared pod legend */}
      {podEntries.length > 0 && (
        <div className="flex max-h-20 flex-wrap items-center gap-x-3 gap-y-1.5 overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              showAll();
              if (variantFilterActive) p.selectAllVariants();
            }}
            disabled={allPodsVisible}
            className="text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            show all
          </button>
          {podEntries.map(([pod, color]) => {
            const variantHidden = isVariantHidden(pod);
            const explicitlyHidden = hiddenPods.has(pod);
            const grayed = variantHidden || explicitlyHidden;
            const onClick = () => {
              if (variantHidden) {
                // Bring this pod's whole variant back into the filter so the
                // chip becomes meaningful again.
                p.toggleVariant(podVariant(pod));
                return;
              }
              setHidden(pod, !explicitlyHidden);
            };
            return (
              <button
                key={pod}
                type="button"
                onClick={onClick}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  if (variantFilterActive) p.selectAllVariants();
                  isolatePod(pod);
                }}
                title={`Click: toggle • Double-click: isolate ${pod}`}
                className={`flex items-center gap-1.5 text-[11px] font-mono select-none transition-opacity ${grayed ? 'opacity-30' : 'opacity-100'}`}
              >
                <span
                  className="inline-block h-[3px] w-3.5 rounded-sm"
                  style={{ background: color }}
                />
                {pod}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
